import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'

import { ApiError, get, post } from '@/api/client'
import { isMfaChallenge, useAuth, type Me, type MfaChallenge } from '@/lib/auth'
import { PasswordField } from './PasswordField'

import '@/styles/pages/login.css'
import '@/styles/pages/auth-sso.css'
import { useBodyClass } from '@/hooks/useBodyClass'
import { LogoMark } from '@/components/Logo'
import { BrandLogo } from '@/components/BrandLogo'

/** Why a Microsoft or Google sign-in came back to this page (app/api/v1/sso.py). */
const SSO_ERRORS: Record<string, string> = {
  no_account:
    'This user is not found in the application. Kindly request access using the Request access link below.',
  not_verified: 'That account does not have a verified email address, so it cannot be used to sign in.',
  cancelled: 'Sign-in was cancelled.',
  expired: 'That sign-in took too long or started in another browser. Please try again.',
  not_configured: 'That sign-in option is not set up on this server.',
  failed: 'Sign-in with that provider did not work. Please try again, or use your password.',
}

function PasskeyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="7.5" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6.5 6.5-6.5 1.4 0 2.6.4 3.7 1.1" />
      <circle cx="17.5" cy="13.5" r="2.5" />
      <path d="M17.5 16v5l1.5-1.2M17.5 18.5l1.5-1" />
    </svg>
  )
}

export default function LoginPage() {
  useBodyClass('ix-body-login')

  const { login, verifyMfa, setMe } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const notice = (location.state as { notice?: string } | null)?.notice ?? null
  const ssoError = SSO_ERRORS[new URLSearchParams(location.search).get('sso_error') ?? ''] ?? null

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // "Forgot password?" was an href="#" for the life of the product. It is a
  // real step now, and it is only offered where a link could actually be
  // delivered - a form that takes an address and sends nothing is worse than
  // no link at all.
  const [resetting, setResetting] = useState(false)
  const [resetSent, setResetSent] = useState<string | null>(null)
  const tenant = useQuery({
    queryKey: ['auth', 'tenant'],
    queryFn: () => get<{ canResetPassword: boolean }>('/auth/tenant'),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const requestReset = useMutation({
    mutationFn: () => post<{ message: string }>('/auth/password-reset', { email: email.trim().toLowerCase() }),
    onSuccess: (r) => setResetSent(r.message),
  })

  // Second step, only for accounts with two-factor authentication on.
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)

  const passkeysSupported = typeof window !== 'undefined' && browserSupportsWebAuthn()
  const providers = useQuery({
    queryKey: ['auth', 'sso-providers'],
    queryFn: () => get<{ microsoft: boolean; google: boolean }>('/auth/sso/providers'),
    staleTime: 5 * 60_000,
    retry: false,
  })

  useEffect(() => {
    document.title = 'Sign in · Insuronix'
  }, [])

  // Microsoft/Google sign-in for an account with two-factor on arrives as #mfa=<challenge>.
  useEffect(() => {
    const match = /^#mfa=([\w.-]+)$/.exec(window.location.hash)
    if (!match) return
    setChallenge({ mfaRequired: true, challenge: match[1], expiresIn: 300 })
    window.history.replaceState(null, '', window.location.pathname)
  }, [])

  const enter = (me: Me) => {
    navigate(me.onboardingCompleted ? '/dashboard' : '/onboarding/source', { replace: true })
  }

  const signIn = useMutation({
    mutationFn: () => login(email, password),
    onSuccess: (result) => {
      if (isMfaChallenge(result)) {
        setChallenge(result)
        setCode('')
        setUseRecovery(false)
        return
      }
      enter(result)
    },
  })

  const passkey = useMutation({
    mutationFn: async () => {
      const begin = await post<{ options: PublicKeyCredentialRequestOptionsJSON; state: string }>('/auth/passkey/options')
      const credential = await startAuthentication({ optionsJSON: begin.options })
      return post<Me>('/auth/passkey/verify', { state: begin.state, credential })
    },
    onSuccess: (me) => {
      setMe(me)
      enter(me)
    },
  })

  const verify = useMutation({
    mutationFn: () =>
      verifyMfa(
        useRecovery
          ? { challenge: challenge?.challenge ?? '', recoveryCode: code.trim() }
          : { challenge: challenge?.challenge ?? '', code: code.replace(/\s/g, '') },
      ),
    onSuccess: enter,
  })

  const startOver = () => {
    setChallenge(null)
    setPassword('')
    setCode('')
    signIn.reset()
    verify.reset()
  }

  if (resetting) {
    return (
      <main className="au-card">
        <div className="au-head">
          <div className="au-brand">
            <LogoMark variant="auth" />
          </div>
          <div className="au-sub">Reset your password</div>
        </div>

        {resetSent ? (
          <>
            {/* The same sentence whatever happened - an address with an
                account, one without, a deactivated account, one belonging to
                another carrier. Any difference is a way to ask who has an
                account here. */}
            <div className="ix-alert ix-alert--success" role="status">
              <div>{resetSent}</div>
            </div>
            <div className="au-alt">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setResetting(false)
                  setResetSent(null)
                }}
              >
                Back to sign in
              </a>
            </div>
          </>
        ) : (
          <>
            <form
              className="au-form"
              onSubmit={(e) => {
                e.preventDefault()
                if (requestReset.isPending || !email.trim()) return
                requestReset.mutate()
              }}
            >
              <div className="au-field">
                <label className="au-label" htmlFor="reset-email">
                  Email address
                </label>
                <input
                  className="au-input"
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="username"
                  autoFocus
                  required
                />
                <div className="ix-caption ix-text-muted">
                  The link lasts 24 hours. Your current password keeps working until you use it.
                </div>
              </div>

              <button className="au-submit" type="submit" disabled={requestReset.isPending || !email.trim()}>
                {requestReset.isPending ? 'Sending…' : 'Send the link'}
              </button>
            </form>

            {requestReset.error ? (
              <div className="ix-error-text" role="alert">
                {requestReset.error instanceof ApiError
                  ? requestReset.error.message
                  : 'That could not be sent. Try again shortly.'}
              </div>
            ) : null}

            <div className="au-alt">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setResetting(false)
                }}
              >
                Back to sign in
              </a>
            </div>
          </>
        )}
      </main>
    )
  }


  if (challenge) {
    const error = verify.error ? errorMessage(verify.error) : null
    return (
      <main className="au-card">
        <div className="au-head">
          <div className="au-brand">
            <LogoMark variant="auth" />
          </div>
          <div className="au-sub">Two-factor authentication</div>
        </div>

        <form
          className="au-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (verify.isPending || !code.trim()) return
            verify.mutate()
          }}
        >
          <div className="au-field">
            <div className="au-label-row">
              <label className="au-label" htmlFor="mfa-code">
                {useRecovery ? 'Recovery code' : 'Authentication code'}
              </label>
              <button
                className="au-link-sm"
                type="button"
                style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                onClick={() => {
                  setUseRecovery((v) => !v)
                  setCode('')
                  verify.reset()
                }}
              >
                {useRecovery ? 'Use your authenticator app' : 'Use a recovery code'}
              </button>
            </div>
            <input
              className="au-input"
              id="mfa-code"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={useRecovery ? 24 : 12}
              autoFocus
            />
            <div className="ix-caption ix-text-muted">
              {useRecovery
                ? 'Each recovery code works once.'
                : `Enter the 6-digit code from your authenticator app${email ? ` for ${email}` : ''}.`}
            </div>
          </div>

          <button className="au-submit" type="submit" disabled={verify.isPending || !code.trim()}>
            {verify.isPending ? 'Verifying…' : 'Verify and sign in'}
          </button>
        </form>

        {error ? (
          <div className="ix-error-text" role="alert">
            {error}
          </div>
        ) : null}

        <div className="au-alt">
          Not you?{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              startOver()
            }}
          >
            Start over
          </a>
        </div>
      </main>
    )
  }

  const error = signIn.error ? errorMessage(signIn.error) : passkey.error ? passkeyError(passkey.error) : ssoError
  const showMicrosoft = providers.data?.microsoft === true
  const showGoogle = providers.data?.google === true
  const busy = signIn.isPending || passkey.isPending

  return (
    <main className="au-card">
      <div className="au-head">
        <div className="au-brand">
          <LogoMark variant="auth" />
        </div>
        <div className="au-sub">Sign in to your account</div>
      </div>

      {notice ? (
        <div className="ix-alert ix-alert--success" role="status">
          <div>{notice}</div>
        </div>
      ) : null}

      <form
        className="au-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (busy) return
          passkey.reset()
          signIn.mutate()
        }}
      >
        <div className="au-field">
          <div className="au-label-row">
            <label className="au-label" htmlFor="email">
              Email address
            </label>
          </div>
          <input
            className="au-input"
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="username webauthn"
          />
        </div>

        <div className="au-field">
          <div className="au-label-row">
            <label className="au-label" htmlFor="password">
              Password
            </label>
            {tenant.data?.canResetPassword ? (
              <button
                className="au-link-sm"
                type="button"
                style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                onClick={() => {
                  setResetting(true)
                  setResetSent(null)
                  requestReset.reset()
                }}
              >
                Forgot password?
              </button>
            ) : null}
          </div>
          <PasswordField
            id="password"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>

        <button className="au-submit" type="submit" disabled={busy}>
          {signIn.isPending ? 'Signing in…' : 'Sign In'}
        </button>
      </form>

      {passkeysSupported || showMicrosoft || showGoogle ? (
        <>
          <div className="au-divider">or</div>
          <div className="au-alt-actions">
            {passkeysSupported ? (
              <button
                className="au-alt-btn"
                type="button"
                disabled={busy}
                onClick={() => {
                  signIn.reset()
                  passkey.mutate()
                }}
              >
                <PasskeyIcon />
                {passkey.isPending ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
              </button>
            ) : null}
            {showMicrosoft ? (
              <a className="au-alt-btn" href="/api/v1/auth/sso/microsoft/start" data-same-tab="true">
                <BrandLogo name="microsoft" size={18} />
                Sign in with Microsoft
              </a>
            ) : null}
            {showGoogle ? (
              <a className="au-alt-btn" href="/api/v1/auth/sso/google/start" data-same-tab="true">
                <BrandLogo name="google" size={18} />
                Sign in with Google
              </a>
            ) : null}
          </div>
        </>
      ) : null}

      {error ? (
        <div className="ix-error-text" role="alert">
          {error}
        </div>
      ) : null}

      <div className="au-alt">
        Don’t have an account? <Link to="/signup">Request access</Link>
      </div>

      <a className="au-back" href="https://www.insuronix.ai" data-same-tab="true">
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M13 8H3"></path>
          <path d="M6.5 4.5 3 8l3.5 3.5"></path>
        </svg>
        Back to website
      </a>
    </main>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return err.message || 'Too many attempts. Wait a moment and try again.'
    return err.message || 'Sign in failed. Please try again.'
  }
  if (err instanceof Error && err.message) return err.message
  return 'Sign in failed. Please try again.'
}

function passkeyError(err: unknown): string {
  if (err instanceof ApiError) return errorMessage(err)
  const name = err instanceof Error ? err.name : ''
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'Passkey sign-in was cancelled or timed out.'
  }
  if (name === 'SecurityError') {
    return 'Passkeys need the secure https://localhost address with a trusted certificate.'
  }
  return errorMessage(err)
}
